import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, Calendar, Phone } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { getRecommendationBookingPath, useAIPersonalization } from './AIPersonalizationEngine';
import { SIGNATURE_BOOKING_PATH } from '@/lib/consultationBooking';

// Build CTA config from personalization recommendations + page context
function buildCTA(pageName, recommendations, userProfile) {
  const segment = userProfile?.personalization_segment || 'preventative_care';
  const pageL = (pageName || '').toLowerCase();

  // Emergency always wins
  if (pageL.includes('emergency') || segment === 'emergency_care') {
    return { title: 'Need Immediate Care?', description: 'Call now for same-day emergency appointments', action: 'Call (727) 228-6846', icon: Phone, color: 'red', urgent: true };
  }

  // Use recommendation primary CTA when available
  if (recommendations?.primary_cta) {
    const r = recommendations.primary_cta;
    const isCall = r.action === 'call-now';
    const colorMap = { high: 'amber', medium: 'amber', low: 'slate', urgent: 'red' };
    return {
      title: r.highlight || (isCall ? 'Speak With Our Team' : 'Ready to Transform Your Smile?'),
      description: recommendations.personalized_message || r.text,
      action: r.text,
      icon: isCall ? Phone : Calendar,
      color: colorMap[r.urgency_level] || 'amber',
      urgent: r.urgency_level === 'urgent',
      actionType: r.action,
      href: r.href
    };
  }

  // Fallback: page-based
  if (pageL.includes('implant')) return { title: 'Schedule Your Implant Consultation', description: 'Get 3D imaging & a personalized treatment plan', action: 'Book Signature Consult', icon: Calendar, color: 'amber', href: SIGNATURE_BOOKING_PATH };
  if (pageL.includes('cosmetic') || pageL.includes('veneer')) return { title: 'See Your New Smile First', description: 'Digital smile preview with your consultation', action: 'Get Smile Preview', icon: Calendar, color: 'rose' };
  if (pageL.includes('invisalign')) return { title: 'Start Your Smile Journey', description: 'Book a consultation with digital treatment preview', action: 'Book Consultation', icon: Calendar, color: 'blue' };
  return { title: 'Ready for a Better Smile?', description: 'Schedule your appointment today', action: 'Book Appointment', icon: Calendar, color: 'amber' };
}

export default function SmartCTAEngine({ pageName }) {
  const { recommendations, userProfile, trackInteraction } = useAIPersonalization();
  const [activeCTA, setActiveCTA] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const lastDismissed = parseInt(localStorage.getItem('smartCTA_lastDismissed') || '0', 10);
    if (Date.now() - lastDismissed < 24 * 60 * 60 * 1000) { setDismissed(true); return; }

    // Show earlier for high-intent users
    const isHighIntent = ['high_intent_implants', 'emergency_care'].includes(userProfile?.personalization_segment);
    const delay = isHighIntent ? 12000 : 22000;

    const timer = setTimeout(() => {
      setActiveCTA(buildCTA(pageName, recommendations, userProfile));
    }, delay);

    return () => clearTimeout(timer);
  }, [pageName, recommendations, userProfile]);

  const handleDismiss = () => {
    localStorage.setItem('smartCTA_lastDismissed', Date.now().toString());
    setActiveCTA(null);
    setDismissed(true);
  };

  const handleAction = () => {
    trackInteraction('cta-click', 'smart_cta_popup', { cta_title: activeCTA?.title, page: pageName });
    base44.analytics.track({
      eventName: 'smart_cta_clicked',
      properties: { location: 'smart_cta_popup', page_name: pageName, cta_title: activeCTA?.title }
    });
  };

  if (!activeCTA || dismissed) return null;

  const colorClasses = {
    amber: { bg: 'bg-amber-600', hover: 'hover:bg-amber-700', border: 'border-amber-200', text: 'text-amber-900', bgLight: 'bg-amber-50' },
    rose: { bg: 'bg-rose-600', hover: 'hover:bg-rose-700', border: 'border-rose-200', text: 'text-rose-900', bgLight: 'bg-rose-50' },
    blue: { bg: 'bg-blue-600', hover: 'hover:bg-blue-700', border: 'border-blue-200', text: 'text-blue-900', bgLight: 'bg-blue-50' },
    red: { bg: 'bg-red-600', hover: 'hover:bg-red-700', border: 'border-red-200', text: 'text-red-900', bgLight: 'bg-red-50' }
  };

  const colors = colorClasses[activeCTA.color] || colorClasses.amber;
  const Icon = activeCTA.icon;
  const isCall = ['call', 'call-now', 'call-office'].includes(activeCTA.actionType) || activeCTA.action?.includes('Call');

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-40"
      >
        <div className={`${colors.bgLight} border-2 ${colors.border} rounded-2xl shadow-2xl p-6 relative`}>
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-start gap-4">
            <div className={`${colors.bg} p-3 rounded-xl`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${colors.text} mb-1`}>{activeCTA.title}</h3>
              <p className="text-sm text-slate-600 mb-4">{activeCTA.description}</p>
              <Button
                asChild
                className={`${colors.bg} ${colors.hover} text-white w-full`}
              >
                <a href={isCall ? "tel:7272286846" : getRecommendationBookingPath(activeCTA.href)} onClick={handleAction}>
                  {activeCTA.action}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}